/**
 * EmployeeAccountCard — Admin login/role/password controls on employee detail.
 * Users are no longer a separate nav destination; accounts live with employees.
 */
import { useMemo, useState } from 'react';
import { KeyRound, Shield, UserCircle } from 'lucide-react';
import { Badge } from '../../../shell/Badge.js';
import { Button } from '../../../shell/Button.js';
import { Modal } from '../../../shell/Modal.js';
import { Input, Select } from '../../../shell/Field.js';
import { toast } from '../../../shell/Toast.js';
import { ApiRequestError } from '../../../lib/api.js';
import { ROLE, type Role } from '@shared/enums/index.js';
import { useAuthStore } from '../../../store/auth.js';
import {
  useCreateUser,
  useResetUserPassword,
  useUpdateUserRole,
  useUsers,
  type UserListItem,
} from '../../users/api.js';
import type { Employee } from '@shared/types';

const roleTone: Record<string, 'purple' | 'blue' | 'slate'> = {
  Admin: 'purple',
  Manager: 'blue',
  Employee: 'slate',
};

interface Props {
  employee: Employee;
}

export function EmployeeAccountCard({ employee }: Props) {
  const { user: currentUser } = useAuthStore();
  const { data: usersData, isLoading } = useUsers();
  const createUser = useCreateUser();
  const resetPassword = useResetUserPassword();
  const updateRole = useUpdateUserRole();
  const [resetOpen, setResetOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const account = useMemo(() => {
    const items = usersData?.items ?? [];
    if (employee.userId) {
      return items.find((u) => u.id === employee.userId) ?? null;
    }
    return items.find((u) => u.employeeId === employee.id) ?? null;
  }, [usersData?.items, employee.userId, employee.id]);

  const isSelf = !!account && account.id === currentUser?.id;

  const handleRoleChange = async (role: Role) => {
    if (!account) return;
    try {
      await updateRole.mutateAsync({ id: account.id, role });
      toast('success', `Role updated to ${role}.`);
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to update role.';
      toast('error', msg);
    }
  };

  const handleReset = async (newPassword: string) => {
    if (!account) return;
    try {
      await resetPassword.mutateAsync({ id: account.id, newPassword });
      toast('success', 'Password reset.');
      setResetOpen(false);
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to reset password.';
      toast('error', msg);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 rounded-lg bg-slate-100 p-2 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
            <UserCircle className="h-4 w-4" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Login account</p>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              Employees sign in with this email. Role controls what they can access.
            </p>
          </div>
        </div>
        {account && (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" type="button" onClick={() => setResetOpen(true)}>
              <KeyRound className="h-3.5 w-3.5" /> Reset password
            </Button>
          </div>
        )}
      </div>

      <div className="mt-4">
        {isLoading ? (
          <p className="text-sm text-slate-400">Loading account…</p>
        ) : account ? (
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-400">Email</p>
              <p className="mt-1 text-sm text-slate-800 dark:text-slate-200">{account.email}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-400">Role</p>
              <div className="mt-1 flex items-center gap-2">
                <Badge tone={roleTone[account.role] ?? 'slate'}>{account.role}</Badge>
                {!isSelf && (
                  <select
                    value={account.role}
                    onChange={(e) => handleRoleChange(e.target.value as Role)}
                    className="rounded border border-slate-300 bg-transparent px-1 py-0.5 text-xs text-slate-600 dark:border-slate-700 dark:text-slate-400"
                    aria-label="Change role"
                  >
                    {ROLE.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-400">Status</p>
              <p className="mt-1 inline-flex items-center gap-1 text-sm text-emerald-600 dark:text-emerald-400">
                <Shield className="h-3.5 w-3.5" /> Linked
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-amber-50 px-3 py-2 dark:bg-amber-950/30">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              No login account linked. Create one so this employee can sign in.
            </p>
            <Button size="sm" type="button" onClick={() => setCreateOpen(true)}>
              Create login
            </Button>
          </div>
        )}
      </div>

      {account && resetOpen && (
        <ResetPasswordModal
          user={account}
          loading={resetPassword.isPending}
          onClose={() => setResetOpen(false)}
          onReset={handleReset}
        />
      )}
      {createOpen && (
        <CreateLoginModal
          employee={employee}
          loading={createUser.isPending}
          onClose={() => setCreateOpen(false)}
          onCreate={async ({ password, role }) => {
            try {
              await createUser.mutateAsync({
                email: employee.email,
                name: employee.name,
                password,
                role,
                employeeId: employee.id,
              });
              toast('success', 'Login account created.');
              setCreateOpen(false);
            } catch (err) {
              const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to create login.';
              toast('error', msg);
            }
          }}
        />
      )}
    </div>
  );
}

function ResetPasswordModal({
  user,
  onClose,
  onReset,
  loading,
}: {
  user: UserListItem;
  onClose: () => void;
  onReset: (newPassword: string) => void;
  loading: boolean;
}) {
  const [newPassword, setNewPassword] = useState('');
  return (
    <Modal open onClose={onClose} title={`Reset password — ${user.name}`} size="sm">
      <div className="space-y-4">
        <Input
          label="New password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="Min 8 characters"
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button
            type="button"
            onClick={() => onReset(newPassword)}
            loading={loading}
            disabled={newPassword.length < 8}
          >
            Reset password
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function CreateLoginModal({
  employee,
  onClose,
  onCreate,
  loading,
}: {
  employee: Employee;
  onClose: () => void;
  onCreate: (input: { password: string; role: Role }) => Promise<void>;
  loading: boolean;
}) {
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('Employee');
  return (
    <Modal open onClose={onClose} title={`Create login — ${employee.name}`} size="sm">
      <div className="space-y-4">
        <Input label="Email" value={employee.email} disabled />
        <Select label="Role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
          {ROLE.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </Select>
        <Input
          label="Initial password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Min 8 characters"
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button
            type="button"
            loading={loading}
            disabled={password.length < 8}
            onClick={() => onCreate({ password, role })}
          >
            Create login
          </Button>
        </div>
      </div>
    </Modal>
  );
}
